import { patchState, signalStore, withHooks, withMethods, withState } from '@ngrx/signals'
import { AppointmentDiaryFilter } from '../interfaces/appointment/appointment-diary-filter.interface'
import { setAllEntities, withEntities } from '@ngrx/signals/entities'
import { AppointmentListItem } from '../interfaces/appointment/appointment-list-item.interface'
import { inject } from '@angular/core'
import { AppointmentService } from 'src/app/core/services/appointment/appointment.service'
import { SignalRService } from 'src/app/core/services/signalr/signalr-service'
import { StaffTypeService } from 'src/app/core/services/staff/staff-type.service'
import { StaffService } from 'src/app/core/services/staff/staff.service'
import { tapResponse } from '@ngrx/operators'
import { rxMethod } from '@ngrx/signals/rxjs-interop'
import { pipe, switchMap, forkJoin, distinctUntilChanged } from 'rxjs'
import { StaffListItem } from '../interfaces/staff/staff-list-item.interface'
import { StaffType } from '../interfaces/staff-type/staff-type.interface'
import { AppointmentFilter } from '../interfaces/appointment/appointment-filter.interface'
import { PaginatedList } from '../interfaces/api-response/api-paginated-list.interface'
import { ToastrService } from 'ngx-toastr'

const today = new Date()

interface AppointmentState {
    filter: AppointmentFilter
    appointmentsPaged: PaginatedList<AppointmentListItem> | null
    staff: StaffListItem[]
    staffTypes: StaffType[]
}

const initialState: AppointmentState = {
    filter: {
        pageNumber: 1,
        pageSize: 10,
        staffId: undefined,
        from: new Date(today.getFullYear() - 10, today.getMonth(), today.getDate(), 0, 0, 0).toISOString(),
        to: new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59).toISOString(),
        hideFinishedAppointments: true,
        sortColumn: 'appointmentStart',
        sortOrder: 'asc',
        skipGlobalLoading: false,
    },
    appointmentsPaged: null,
    staff: [],
    staffTypes: [],
}

export const AppointmentStore = signalStore(
    { protectedState: false },
    withState<AppointmentState>(initialState),
    withMethods((store) => {
        const appointmentService = inject(AppointmentService)
        const signalRService = inject(SignalRService)
        const staffTypeService = inject(StaffTypeService)
        const staffService = inject(StaffService)
        const toastr = inject(ToastrService)

        return {
            updateFilter(filter: Partial<AppointmentFilter>) {
                patchState(store, { filter: { ...store.filter(), ...filter } })
            },
            loadInitialData: rxMethod<void>(
                pipe(
                    switchMap(() => {
                        const staff$ = staffService.getAllStaff()
                        const staffTypes$ = staffTypeService.getAllStaffTypes()

                        return forkJoin([staff$, staffTypes$]).pipe(
                            tapResponse({
                                next: ([staff, staffTypes]) => {
                                    patchState(store, {
                                        staff: staff,
                                        staffTypes: staffTypes,
                                    })
                                },
                                error: (error) => toastr.error('Failed to load initial data'),
                            })
                        )
                    })
                )
            ),
            loadByQuery: rxMethod<AppointmentFilter>(
                pipe(
                    distinctUntilChanged(),
                    switchMap((filter) =>
                        appointmentService.getAppointmentsPaged(filter).pipe(
                            tapResponse({
                                next: (response) => {
                                    patchState(store, { appointmentsPaged: response })
                                },
                                error: (error) => toastr.error('Failed to load appointments'),
                            })
                        )
                    )
                )
            ),
        }
    }),
    withHooks({
        onInit(store) {
            store.loadInitialData()
        },
    })
)
